import { combineLatest, Subject, takeUntil } from 'rxjs';

import {
    ChangeDetectionStrategy, Component, forwardRef, inject, QueryList, ViewChildren
} from '@angular/core';
import {
    ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR, ValidatorFn, Validators
} from '@angular/forms';
import { ValidatorsService } from '@services/validators.service';

import { OptionComponent } from '../select/option.component';

@Component({
  selector: 'app-date-inputs',
  templateUrl: './date-inputs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputsComponent),
      multi: true
    }
  ]
})
export class DateInputsComponent implements ControlValueAccessor {
  private onChange = (input: string) => {};
  protected onTouch = (isTouched: boolean) => {};
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  protected readonly isMobile = window.innerWidth < 768;
  @ViewChildren(OptionComponent) optionComponents!: QueryList<OptionComponent>;

  private currentDateValidator!: ValidatorFn;

  private readonly validatorsService = inject(ValidatorsService);
  dateForm = this.fb.group({
    date: ['', [Validators.required]],
    month: ['', [Validators.required, this.validatorsService.monthValidator]],
    year: ['', [Validators.required, this.validatorsService.yearValidator]]
  });
  registerOnChange(fn: (input: string) => void) {
    this.onChange = fn;
  }
  registerOnTouched(fn: (input: boolean) => void) {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean) {
    if (isDisabled) this.dateForm.disable();
    else this.dateForm.enable();
  }
  writeValue(value: string) {
    const controls = this.dateForm.controls;

    if (!value?.length) {
      controls.date.setValue('');
      controls.month.setValue('');
      controls.year.setValue('');
      return;
    }
    if (!this.validatorsService.REGEX.DATE.test(value)) return;
    const [year, month, date] = value.split('-');
    controls.date.setValue(+date + '');
    controls.month.setValue(+month + '');
    controls.year.setValue(year);
    if (!this.optionComponents) return;
    const found = this.optionComponents.find(option => option.value === +month + '');
    if (!found) return;
    found.updateDisplay();
  }

  ngOnInit() {
    const controls = this.dateForm.controls;
    this.currentDateValidator = this.validatorsService.dateValidator(12, 2020);
    controls.date.addValidators(this.currentDateValidator);
    this.dateForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(currentValue => {
      const { date, month, year } = currentValue;
      if (
        !month ||
        !date ||
        !year ||
        controls.date.errors ||
        controls.month.errors ||
        controls.year.errors
      ) {
        this.onChange('');
        return;
      }
      const _date = date.length === 1 ? `0${date}` : date;
      const _month = month.length === 1 ? `0${month}` : month;
      const result = `${year}-${_month}-${_date}`;
      this.onChange(result);
    });

    const monthChanges = controls.month.valueChanges;
    const yearChanges = controls.year.valueChanges;
    combineLatest([monthChanges, yearChanges])
      .pipe(takeUntil(this.destroy$))
      .subscribe(changes => {
        const [month, year] = changes;
        if (!month || !year) return;
        if (controls.date.hasValidator(this.currentDateValidator))
          controls.date.removeValidators(this.currentDateValidator);
        this.currentDateValidator = this.validatorsService.dateValidator(+month, +year);
        controls.date.addValidators(this.currentDateValidator);
        controls.date.setValue(controls.date.value);
      });
  }
  ngOnDestroy() {
    this.destroy$.next();
  }
}
